import { useEffect, Fragment } from 'react';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import DataTable from 'react-data-table-component';
import { Badge } from 'react-bootstrap';
import { Helmet, HelmetProvider } from 'react-helmet-async';

import { formatNumber, sortTableStatusStatus } from '../../utils/generalFunctions';

import { editarBreadcrumb } from '../../actions/breadcrumb';
import { obtenerLocatario, limpiarLocatario, cambioStatusLocatario } from '../../actions/locatarios';

import NuevaRenta from './NuevaRenta';
import EditarRenta from './EditarRenta';
import EliminarRenta from './EliminarRenta';

const DetallesLocatario = () => {

    let rutasBread = [
        {
            activo: false,
            nombre: 'Locatarios',
            path: '/locatarios'
        },
        {
            activo: true,
            nombre: 'Perfil de locatario',
            path: '/locatarios/detalles/'
        }
    ];

    const dispatch = useDispatch();

    const { id } = useParams();

    const { detalles } = useSelector(state => state.locatarios);

    const [locatarioData, setLocatarioData] = useState({
        indicadores_generales: {
            calificacion_promedio: 0,
            citas: 0,
            canjes_realizados: 0,
            porcentaje_canje: 0
        },
        informacion_personal: {
            _id: null,
            nombre: null,
            servicio: null,
            direccion: null,
            soluciones: false,
            descripcion: null,
            status: false,
            contactos: [],
            url_perfil: process.env.REACT_APP_PUBLIC_ROUTE + '/assets/images/perfil_placeholder.png'
        },
        horarios: [],
        calificaciones: []
    });

    /* Rentas */
    const [nuevaRenta, setNuevaRenta] = useState({
        colaborador: null
    });
    const [editarRenta, setEditarRenta] = useState(null);
    const [eliminarRenta, setEliminarRenta] = useState(null);

    const {indicadores_generales, informacion_personal, rentas, servicios, citas, horarios, calificaciones} = locatarioData;

    const paginationOptions = { rowsPerPageText: 'Filas por página', rangeSeparatorText: 'de' };

    const rentasColumnas = [
        {
            name: 'Fecha', 
            sortable: true, 
            cell: item => item.fecha ? new Date(item.fecha).toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' }) : 'Sin fecha'
        },
        {
            name: 'Total',
            selector: row => formatNumber(row.total, 2, '$ ', '')
        },
        {
            name: 'Método de pago',
            sortable: true, 
            selector: row => {
                switch(row.metodo){
                    case 0:
                        return 'Efectivo';
                    case 1:
                        return 'Tarjeta';
                    case 2:
                        return 'Transferencia';
                    default:
                        break;
                }
            },
        }, 
        {
            name: 'Observaciones',
            grow: 3, 
            wrap: true, 
            selector: row => row.observaciones
        }, 
        {
            name: '',
            grow: 1, 
            cell: row => {
                return(
                    <div className="text-right w-100">
                        <button type="button" className="btn btn-sm btn-link px-2 float-end" onClick={() => handleEliminarRenta({...row, id_locatario: id})}><i className="fas fa-times text-danger"></i></button>
                        <button type="button" className="btn btn-sm btn-link px-2 float-end" onClick={() => handleEditarRenta({...row, id_locatario: id})}><i className="fas fa-edit text-primary"></i></button>
                    </div>
                )
            }
        }
    ];

    const serviciosColumnas = [
        {
            name: 'Nombre',
            selector: row => row.nombre,
        },
        {
            name: 'Precio',
            selector: row => formatNumber(row.precio, 2, '$ ', '')
        },
        {
            name: 'Duración de cita',
            selector: row => {
                switch(row.intervalo_cita){
                    case 15: 
                        return '15 minutos';
                    case 30: 
                        return 'Media hora';
                    case 45: 
                        return '45 minutos';
                    case 60: 
                        return '1 hora';
                    case 90: 
                        return 'Hora y media';
                    case 120: 
                        return '2 horas';
                    case 150: 
                        return '2 horas y media';
                    default:
                        return 'Sin definir';
                }
            }
        }, 
    ];

    const citasColumnas = [
        {
            name: 'Cliente',
            sortable: true, 
            selector: row => row.cliente ? [row.cliente?.nombre, row.cliente?.apellido_paterno, row.cliente?.apellido_materno].join(' ') : 'Sin definir', 
            grow: 2
        },
        {
            name: 'Fecha de inicio',
            sortable: true, 
            cell: item => new Date(item.fecha_inicio).toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' }) + ' a las ' + new Date(item.fecha_inicio).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }), 
            grow: 2
        },
        {
            name: 'Fecha de finalización',
            sortable: true, 
            cell: item => new Date(item.fecha_fin).toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' }) + ' a las ' + new Date(item.fecha_fin).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }), 
            grow: 2
        },
        {
            name: 'Status',
            sortable: true, 
            sortFunction: sortTableStatusStatus, 
            selector: row => {
                switch(row.status){
                    case 0:
                        return(
                            <h5><Badge pill bg="warning" className="text-dark">Agendada</Badge></h5>
                        )
                    case 1:
                        return(
                            <h5><Badge pill bg="success">Autorizada</Badge></h5>
                        )
                    case 2:
                        return(
                            <h5><Badge pill bg="danger">Cancelada</Badge></h5>
                        )
                    default:
                        break;
                }
            }
        }
    ];

    const horariosColumnas = [
        {
            name: 'Día de la semana',
            selector: row => {
                switch(row.dia){
                    case 0: 
                        return 'Domingo';
                    case 1: 
                        return 'Lunes';
                    case 2: 
                        return 'Martes';
                    case 3: 
                        return 'Miercole';
                    case 4: 
                        return 'Jueves';
                    case 5: 
                        return 'Viernes';
                    case 6: 
                        return 'Sabado';
                    default:
                        break;
                }
            }
        }, 
        {
            name: 'Hora inicial',
            selector: row => row.hora_inicio
        },
        {
            name: 'Hora final',
            selector: row => row.hora_fin
        },
    ];

    const calificacionesColumnas = [
        {
            name: 'Joven',
            sortable: true, 
            selector: row => row.joven ? [row.joven.nombre, row.joven.apellido_paterno, row.joven.apellido_materno].join(' ') : 'Sin definir', 
            grow: 2
        },
        {
            name: 'Comentario',
            sortable: true, 
            selector: row => row.comentario,
            grow: 5
        },
        {
            name: 'Calificación',
            sortable: true, 
            selector: row => row.calificacion
        }
    ];

    /* Rentas */
    const handleAgregarRenta = (locatario) => {
        setNuevaRenta({
            locatario, 
            total: informacion_personal?.monto_renta
        });
    }

    const handleEditarRenta = (renta) => {
        setEditarRenta(renta);
    }

    const handleEliminarRenta = (renta) => {
        setEliminarRenta(renta);
    }

    /* Funciones generales */
    const handleCambioStatus = () => {
        if(informacion_personal?.status){
            dispatch(cambioStatusLocatario(id, false));
        }else{
            dispatch(cambioStatusLocatario(id, true));
        }
    }

    useEffect(() => {
        dispatch(limpiarLocatario());

        return () => {
            dispatch(limpiarLocatario());
        }
    }, []);

    useEffect(() => {        
        if(id){
            dispatch(obtenerLocatario(id));

            rutasBread[1].path = rutasBread[1].path + id;

            dispatch(editarBreadcrumb(rutasBread));
        }
    }, [id]);

    useEffect(() => {
        if(detalles){
            setLocatarioData({
                ...locatarioData,
                indicadores_generales: detalles.indicadores_generales, 
                informacion_personal: detalles.informacion_personal, 
                rentas: detalles.rentas, 
                servicios: detalles.servicios, 
                citas: detalles.citas, 
                horarios: detalles.horarios, 
                calificaciones: detalles.calificaciones
            });
        }
    }, [detalles]);

    return (
        <Fragment>
            <HelmetProvider>
                <Helmet>
                    <style>{`
                        .contenedor_imagen_perfil {
                            border-radius: 50%;
                            display: inline-block;
                            position: relative;
                            padding: 0px;
                            background: #fff;
                            border: 5px solid #111F62;
                        }
                        
                        .contenedor_imagen{
                            position: relative;
                            width: 200px;
                            height: 200px;
                            overflow: hidden;
                            border-radius: 50%;
                        }

                        #imagen_perfil {
                            width: auto;
                            height: 100%;
                            display: block;
                            position: absolute;
                            margin: auto;
                            top: -100%;
                            right: -100%;
                            bottom: -100%;
                            left: -100%;
                        }

                        .btn-badge-status{
                            background-color: #fff;
                            border: none;
                            outline: none;
                            padding: 0;
                        }

                        .valor_indicador {
                            font-size: 40px;
                            margin: 0;
                        }

                        .descripcion_indicador {
                            margin: 0;
                        }

                        .textareCampo {
                            height: 100px !important;
                        }
                    `}</style>
                </Helmet>
            </HelmetProvider>

            <NuevaRenta renta={nuevaRenta} reestablecerRenta={handleAgregarRenta} />
            <EditarRenta renta={editarRenta} reestablecerRenta={handleEditarRenta} />
            <EliminarRenta renta={eliminarRenta} reestablecerRenta={handleEliminarRenta} />

            <div className="row">
                <div className="col-md-10 offset-md-1 pb-4">
                    <h3 className="my-5">Perfil de locatario</h3>
                    
                    <div className="row mb-4">
                        <div className="col-md text-center">
                            <p className="valor_indicador">{ formatNumber(indicadores_generales?.calificacion_promedio, 0, '', '') }</p>
                            <p className="descripcion_indicador">Calificación</p>
                        </div>
                        <div className="col-md text-center">
                            <p className="valor_indicador">{ formatNumber(indicadores_generales?.citas, 0, '', '') }</p>
                            <p className="descripcion_indicador">Citas</p>
                        </div>
                        <div className="col-md text-center">
                            <p className="valor_indicador">{ formatNumber(indicadores_generales?.ingresos, 0, '', '') }</p>
                            <p className="descripcion_indicador">Ingresos generados</p>
                        </div>
                        <div className="col-md text-center">
                            <p className="valor_indicador">{ formatNumber(indicadores_generales?.comisiones, 0, '', '') }</p>
                            <p className="descripcion_indicador">Comisiones obtenidas</p>
                        </div>
                        <div className="col-md text-center">
                            <div className="mt-2 d-flex justify-content-center">
                                <button className="btn-badge-status" onClick={() => handleCambioStatus()}>
                                    <h3 className="text-muted">
                                        <Badge bg={informacion_personal?.status ? 'success' : 'danger'}>
                                            {informacion_personal?.status ? 'Activo' : 'Inactivo'}
                                        </Badge>
                                    </h3>
                                </button>
                            </div>
                            <p className="descripcion_indicador">Status</p>
                        </div>
                    </div>
                    
                    <div className="row">
                        <div className="col-md-12">
                            <div className="accordion" id="accordionDetalles">
                                <div className="accordion-item">
                                    <h2 className="accordion-header" id="headingOne">
                                        <button className="accordion-button" type="button" data-bs-toggle="collapse" data-bs-target="#collapseOne" aria-expanded="true" aria-controls="collapseOne">
                                            Información de perfil
                                        </button>
                                    </h2>
                                    <div id="collapseOne" className="accordion-collapse collapse show" aria-labelledby="headingOne" data-bs-parent="#accordionDetalles">
                                        <div className="accordion-body row">
                                            <div className="col-md-3">
                                                <label className="fw-bold">Nombre</label>
                                                <p>{informacion_personal?.nombre}</p>
                                            </div>
                                            <div className="col-md-3">
                                                <label className="fw-bold">Categoria de servicio</label>
                                                <p>{informacion_personal?.servicio?.nombre}</p>
                                            </div>
                                            <div className="col-md-2">
                                                <label className="fw-bold">Renta mensual</label>
                                                <p>{formatNumber(informacion_personal?.monto_renta, 0, '$', '')}</p>
                                            </div>
                                            <div className="col-md-2">
                                                <label className="fw-bold">Fecha de registro</label>
                                                <p>{new Date(informacion_personal?.creacion).toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
                                            </div>
                                            <div className="col-md-2">
                                                <label className="fw-bold">Métodos de pago</label>
                                                <p>
                                                    {informacion_personal?.metodos_pago?.length
                                                        ? informacion_personal.metodos_pago.map(m => m === 0 ? 'Efectivo' : m === 1 ? 'Tarjeta' : 'Transferencia').join(', ')
                                                        : 'Sin definir'
                                                    }
                                                </p>
                                            </div>

                                            <div className='col-md-12'>
                                                <label className='fw-bold'>Descripción</label>
                                                <p>{informacion_personal?.descripcion}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="accordion-item">
                                    <h2 className="accordion-header" id="headingTwo">
                                        <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#collapseTwo" aria-expanded="true" aria-controls="collapseTwo">
                                            Historial de pagos de renta
                                        </button>
                                    </h2>
                                    <div id="collapseTwo" className="accordion-collapse collapse" aria-labelledby="headingTwo" data-bs-parent="#accordionDetalles">
                                        <div className="accordion-body row">
                                            <div className="col-md-12 text-end mb-3">
                                                <div className="btn-group" role="group" aria-label="Button group with nested dropdown">
                                                    <button type="button" className="btn btn-primary" onClick={() => handleAgregarRenta(id)}><i className="fa-solid fa-plus"></i> Pago de renta</button>
                                                </div>
                                            </div>

                                            <DataTable
                                                columns={rentasColumnas}
                                                data={rentas}
                                                noDataComponent="No hay pagos de rentas registrados"
                                                pagination
                                                paginationPerPage={10}
                                                paginationRowsPerPageOptions={[10, 20, 50]}
                                                paginationComponentOptions={paginationOptions}
                                                highlightOnHover={true}
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="accordion-item">
                                    <h2 className="accordion-header" id="headingThree">
                                        <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#collapseThree" aria-expanded="true" aria-controls="collapseThree">
                                            Servicios ofrecidos
                                        </button>
                                    </h2>
                                    <div id="collapseThree" className="accordion-collapse collapse" aria-labelledby="headingThree" data-bs-parent="#accordionDetalles">
                                        <div className="accordion-body">
                                            <DataTable
                                                columns={serviciosColumnas}
                                                data={servicios}
                                                noDataComponent="No hay servicios registrados"
                                                pagination
                                                paginationPerPage={10}
                                                paginationRowsPerPageOptions={[10, 20, 50]}
                                                paginationComponentOptions={paginationOptions}
                                                highlightOnHover={true}
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="accordion-item">
                                    <h2 className="accordion-header" id="headingFour">
                                        <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#collapseFour" aria-expanded="true" aria-controls="collapseFour">
                                            Citas realizadas
                                        </button>
                                    </h2>
                                    <div id="collapseFour" className="accordion-collapse collapse" aria-labelledby="headingFour" data-bs-parent="#accordionDetalles">
                                        <div className="accordion-body">
                                            <DataTable
                                                columns={citasColumnas}
                                                data={citas}
                                                noDataComponent="No hay citas registradas"
                                                pagination
                                                paginationPerPage={10}
                                                paginationRowsPerPageOptions={[10, 20, 50]}
                                                paginationComponentOptions={paginationOptions}
                                                highlightOnHover={true}
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="accordion-item">
                                    <h2 className="accordion-header" id="headingFive">
                                        <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#collapseFive" aria-expanded="true" aria-controls="collapseFive">
                                            Horarios de atención
                                        </button>
                                    </h2>
                                    <div id="collapseFive" className="accordion-collapse collapse" aria-labelledby="headingFive" data-bs-parent="#accordionDetalles">
                                        <div className="accordion-body">
                                            <DataTable
                                                columns={horariosColumnas}
                                                data={horarios}
                                                noDataComponent="No hay horarios de atención registrados"
                                                pagination
                                                paginationPerPage={10}
                                                paginationRowsPerPageOptions={[10, 20, 50]}
                                                paginationComponentOptions={paginationOptions}
                                                highlightOnHover={true}
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="accordion-item">
                                    <h2 className="accordion-header" id="headingSix">
                                    <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#collapseSix" aria-expanded="true" aria-controls="collapseSix">
                                        Calificaciones y comentarios
                                    </button>
                                    </h2>
                                    <div id="collapseSix" className="accordion-collapse collapse" aria-labelledby="headingSix" data-bs-parent="#accordionDetalles">
                                        <div className="accordion-body">
                                            <DataTable
                                                columns={calificacionesColumnas}
                                                data={calificaciones}
                                                noDataComponent="No hay calificaciones registradas"
                                                pagination
                                                paginationPerPage={10}
                                                paginationRowsPerPageOptions={[10, 20, 50]}
                                                paginationComponentOptions={paginationOptions}
                                                highlightOnHover={true}
                                            />
                                        </div>
                                    </div>
                                </div>

                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Fragment>
    )
}

export default DetallesLocatario;